import CardHeader from "components/CardHeader";
import MinorUnitInput from "components/MinorUnitInput";
import {
    Account,
    AccountAddress,
    AccountBankAccount,
    AccountBillingSettings,
    AccountWorkerRates,
    AccountRole
} from "models/Account";
import { Tenant } from "models/Tenant";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, Input, Spinner } from "reactstrap";
import { KvkCheckResult, kvkResultToAddress, VatCheckResult, vatResultToAddress } from "./AccountFormCard";
import InvoiceIntervalField, { describeInvoiceInterval } from "./InvoiceIntervalField";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";
import { isValidIban, normalizeIban } from "common/Iban";
import { normalizeInterval } from "models/Interval";

type Section = "account" | "addresses" | "bank_accounts" | "billing" | "worker_rates";

interface Props {
    account: Account;
    tenants: Tenant[];
    saving: boolean;
    vatLookup?: (vatNumber: string) => Promise<VatCheckResult>;
    kvkLookup?: (kvkNumber: string, refresh?: boolean) => Promise<KvkCheckResult>;
    onSave: (account: Account) => Promise<boolean>;
}

interface SectionCardProps {
    title: OfficeTranslationKey;
    icon: string;
    editing: boolean;
    saving: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    children: React.ReactNode;
}

const emptyAddress = (): AccountAddress => ({
    label: "",
    street: "",
    number: "",
    addition: "",
    postal_code: "",
    city: "",
    region: "",
    country: "NL",
    primary: true,
});

const emptyBankAccount = (): AccountBankAccount => ({
    label: "",
    iban: "",
    bic: "",
    account_name: "",
    primary: true,
});

const cloneAccount = (account: Account): Account => Object.assign(new Account(), {
    ...account,
    trade_names: [...(account.trade_names ?? [])],
    roles: [...(account.roles ?? [])],
    addresses: (account.addresses ?? []).map(address => ({ ...address })),
    bank_accounts: (account.bank_accounts ?? []).map(bank => ({ ...bank })),
    persons: (account.persons ?? []).map(person => ({ ...person })),
    billing: {
        ...new Account().billing,
        ...(account.billing ?? {}),
        invoice_interval: normalizeInterval(account.billing?.invoice_interval),
    },
    worker_rates: { ...new Account().worker_rates, ...(account.worker_rates ?? {}) },
});

const SectionCard: React.FC<SectionCardProps> = ({
    title,
    icon,
    editing,
    saving,
    onEdit,
    onCancel,
    onSave,
    children,
}) => {
    const { t } = useOfficeTranslation();
    return <Card className="account-detail-card"><CardBody>
        <CardHeader
            title={t(title)}
            icon={icon}
            actions={!editing && <Button color="light" size="sm" type="button" onClick={onEdit} aria-label={t("accounts.accountDetailsCards.editSection", {section: t(title)})}>
                <i className="mdi mdi-pencil-outline me-1" />
                {t("accounts.accountDetailsCards.edit")}
            </Button>}
        />
        <div className={editing ? "pt-3" : ""}>{children}</div>
        {editing && <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" size="sm" type="button" disabled={saving} onClick={onCancel}>{t("common.cancel")}</Button>
            <Button color="primary" size="sm" type="button" disabled={saving} onClick={onSave}>
                {saving ? <><Spinner size="sm" className="me-1" />{t("common.saving")}</> : t("common.save")}
            </Button>
        </div>}
    </CardBody></Card>;
};

const Detail: React.FC<{ label: OfficeTranslationKey; value?: React.ReactNode }> = ({ label, value }) => {
    const { t } = useOfficeTranslation();
    return <div className="d-flex justify-content-between gap-3 border-bottom py-2">
        <span className="text-muted">{t(label)}</span>
        <div className="text-end text-break">{value || "—"}</div>
    </div>;
};

const Field: React.FC<{
    label: OfficeTranslationKey;
    value: string | number;
    type?: "text" | "email" | "number";
    onChange: (value: string) => void;
}> = ({ label, value, type = "text", onChange }) => {
    const { t } = useOfficeTranslation();
    return <label className="form-label d-block mb-3">
    {t(label)}
    <Input className="mt-2" type={type} value={value} onChange={event => onChange(event.target.value)} />
</label>;
};

const normalizeVatText = (value: string): string =>
    value.toLocaleLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeAddressValue = (field: keyof AccountAddress, value: unknown): string => {
    const normalized = normalizeVatText(String(value ?? ""));
    if (field === "country" && ["nl", "nederland", "netherlands", "thenetherlands"].includes(normalized)) {
        return "nl";
    }
    return normalized;
};

export const compareVatResult = (account: Account, result: VatCheckResult) => {
    const registeredName = result.name?.trim() === "---" ? "" : result.name?.trim() ?? "";
    const registeredAddress = vatResultToAddress(result, account.vat_number);
    return compareRegisteredDetails(account, registeredName, registeredAddress);
};

export const compareKvkResult = (account: Account, result: KvkCheckResult) => {
    const registeredName = result.name?.trim() ?? "";
    const registeredAddress = kvkResultToAddress(result);
    return compareRegisteredDetails(account, registeredName, registeredAddress);
};

type ComparableAddressField = "street" | "number" | "addition" | "postal_code" | "city" | "country";

const compareRegisteredDetails = (account: Account, registeredName: string, registeredAddress: Partial<AccountAddress>) => {
    const primaryAddress = account.addresses.find(address => address.primary) ?? account.addresses[0];
    const addressFields: ComparableAddressField[] = ["street", "number", "addition", "postal_code", "city", "country"];
    const providedAddressFields = addressFields.filter(field => String(registeredAddress[field] ?? "").trim() !== "");
    const nameMatches = registeredName ? normalizeVatText(registeredName) === normalizeVatText(account.legal_name) : null;
    const addressMismatches = providedAddressFields
        .filter(field => !primaryAddress ||
            normalizeAddressValue(field, registeredAddress[field]) !== normalizeAddressValue(field, primaryAddress[field]))
        .map(field => ({
            field,
            saved: String(primaryAddress?.[field] ?? ""),
            registered: String(registeredAddress[field] ?? ""),
        }));
    return {
        registeredName,
        registeredAddress,
        nameMatches,
        addressMatches: providedAddressFields.length ? addressMismatches.length === 0 : null,
        mismatches: [
            ...(nameMatches === false ? [{
                field: "legal_name" as const,
                saved: account.legal_name,
                registered: registeredName,
            }] : []),
            ...addressMismatches,
        ],
    };
};

type RegisteredDetailsComparison = ReturnType<typeof compareRegisteredDetails>;

const mismatchLabels: Record<RegisteredDetailsComparison["mismatches"][number]["field"], OfficeTranslationKey> = {
    legal_name: "accounts.accountFormCard.legalName",
    street: "common.street",
    number: "common.number",
    addition: "accounts.accountFormCard.addition",
    postal_code: "accounts.accountFormCard.postalCode",
    city: "common.city",
    country: "accounts.accountFormCard.country",
};

const MismatchDetails: React.FC<{
    comparison: RegisteredDetailsComparison;
    title: OfficeTranslationKey;
}> = ({ comparison, title }) => {
    const { t } = useOfficeTranslation();
    if (!comparison.mismatches.length) return null;

    return <div className="small text-warning mt-1">
        <div className="fw-semibold">{t(title)}</div>
        <div>{t("accounts.accountDetailsCards.savedToRegistration")}</div>
        {comparison.mismatches.map(mismatch => {
            const description = `${t(mismatchLabels[mismatch.field])}: ${mismatch.saved || "—"} → ${mismatch.registered || "—"}`;
            return <div key={mismatch.field} aria-label={description}>{description}</div>;
        })}
    </div>;
};

const AccountDetailsCards: React.FC<Props> = ({ account, tenants, saving, vatLookup, kvkLookup, onSave }) => {
    const { intlLocale, locale, t } = useOfficeTranslation();
    const [editing, setEditing] = useState<Section | null>(null);
    const [draft, setDraft] = useState(() => cloneAccount(account));
    const [checkingVat, setCheckingVat] = useState(false);
    const [vatResult, setVatResult] = useState<VatCheckResult | null>(null);
    const [vatError, setVatError] = useState("");
    const [checkingKvk, setCheckingKvk] = useState(false);
    const [kvkResult, setKvkResult] = useState<KvkCheckResult | null>(null);
    const [kvkError, setKvkError] = useState("");
    const invoiceInterval = normalizeInterval(account.billing?.invoice_interval);

    useEffect(() => {
        if (!editing) setDraft(cloneAccount(account));
    }, [account, editing]);

    useEffect(() => {
        const vatNumber = account.vat_number.trim();
        if (!vatLookup || !vatNumber) {
            setCheckingVat(false);
            setVatResult(null);
            setVatError("");
            return;
        }

        let active = true;
        setCheckingVat(true);
        setVatResult(null);
        setVatError("");
        vatLookup(vatNumber)
            .then(result => {
                if (active) setVatResult(result);
            })
            .catch(() => {
                if (active) setVatError(t("accounts.accountFormCard.vatNumberCouldNotBeChecked"));
            })
            .finally(() => {
                if (active) setCheckingVat(false);
            });
        return () => { active = false; };
    }, [account.id, account.vat_number, vatLookup, t]);

    useEffect(() => {
        const kvkNumber = account.chamber_of_commerce_number.trim();
        if (!kvkLookup || !kvkNumber) {
            setCheckingKvk(false);
            setKvkResult(null);
            setKvkError("");
            return;
        }

        let active = true;
        setCheckingKvk(true);
        setKvkResult(null);
        setKvkError("");
        kvkLookup(kvkNumber)
            .then(result => {
                if (active) setKvkResult(result);
            })
            .catch(() => {
                if (active) setKvkError(t("accounts.accountFormCard.kvkNumberCouldNotBeChecked"));
            })
            .finally(() => {
                if (active) setCheckingKvk(false);
            });
        return () => { active = false; };
    }, [account.id, account.chamber_of_commerce_number, kvkLookup, t]);

    const startEditing = (section: Section) => {
        setDraft(cloneAccount(account));
        setEditing(section);
    };
    const cancel = () => {
        setDraft(cloneAccount(account));
        setEditing(null);
    };
    const save = async () => {
        if (await onSave(draft)) setEditing(null);
    };
    const set = <K extends keyof Account>(field: K, value: Account[K]) =>
        setDraft(current => Object.assign(new Account(), { ...current, [field]: value }));
    const setAddress = (index: number, field: keyof AccountAddress, value: string | boolean) =>
        set("addresses", draft.addresses.map((address, current) =>
            current === index ? { ...address, [field]: value } : address
        ));
    const setBank = (index: number, field: keyof AccountBankAccount, value: string | boolean) =>
        set("bank_accounts", draft.bank_accounts.map((bank, current) =>
            current === index ? { ...bank, [field]: value } : bank
        ));
    const setBilling = <K extends keyof AccountBillingSettings>(field: K, value: AccountBillingSettings[K]) =>
        set("billing", { ...draft.billing, [field]: value });
    const setWorkerRates = <K extends keyof AccountWorkerRates>(field: K, value: AccountWorkerRates[K]) =>
        set("worker_rates", { ...draft.worker_rates, [field]: value });
    const toggleRole = (role: AccountRole) =>
        set("roles", draft.roles.includes(role)
            ? draft.roles.filter(current => current !== role)
            : [...draft.roles, role]);

    const tenantName = tenants.find(tenant => tenant.id === account.tenant)?.name ?? account.tenant;
    const checkVat = async () => {
        if (!vatLookup || !draft.vat_number.trim()) return;
        setCheckingVat(true);
        setVatResult(null);
        setVatError("");
        try {
            const result = await vatLookup(draft.vat_number.trim());
            setVatResult(result);
        } catch {
            setVatError(t("accounts.accountFormCard.vatNumberCouldNotBeChecked"));
        } finally {
            setCheckingVat(false);
        }
    };
    const checkKvk = async () => {
        if (!kvkLookup || !draft.chamber_of_commerce_number.trim()) return;
        setCheckingKvk(true);
        setKvkResult(null);
        setKvkError("");
        try {
            const result = await kvkLookup(draft.chamber_of_commerce_number.trim(), true);
            setKvkResult(result);
        } catch {
            setKvkError(t("accounts.accountFormCard.kvkNumberCouldNotBeChecked"));
        } finally {
            setCheckingKvk(false);
        }
    };
    const vatComparison = vatResult?.isValid ? compareVatResult(draft, vatResult) : null;
    const accountVatComparison = vatResult?.isValid ? compareVatResult(account, vatResult) : null;
    const kvkComparison = kvkResult?.isValid ? compareKvkResult(draft, kvkResult) : null;
    const accountKvkComparison = kvkResult?.isValid ? compareKvkResult(account, kvkResult) : null;
    const vatStatus = !account.vat_number
        ? null
        : checkingVat
            ? <Spinner size="sm" color="primary" aria-label={t("accounts.accountDetailsCards.checkingVatDetails")} title={t("accounts.accountDetailsCards.checkingVatDetails")} />
            : vatError
                ? <i className="mdi mdi-alert-circle-outline text-danger" aria-label={t("accounts.accountDetailsCards.vatCheckFailed")} title={vatError} />
                : vatResult && !vatResult.isValid
                    ? <i className="mdi mdi-close-circle-outline text-danger" aria-label={t("accounts.accountDetailsCards.vatNumberIsInvalid")} title={t("accounts.accountDetailsCards.vatNumberIsInvalid")} />
                    : accountVatComparison?.nameMatches === true && accountVatComparison.addressMatches === true
                        ? <i className="mdi mdi-check-circle text-success" aria-label={t("accounts.accountDetailsCards.vatDetailsMatch")} title={t("accounts.accountDetailsCards.companyNameAndAddressMatchTheVatRegistration")} />
                        : vatResult
                            ? <i className="mdi mdi-alert-circle text-warning" aria-label={t("accounts.accountDetailsCards.vatDetailsDiffer")} title={t("accounts.accountDetailsCards.vatNumberIsValidButTheCompanyNameOrAddressDiffers")} />
                            : null;
    const kvkStatus = !account.chamber_of_commerce_number
        ? null
        : checkingKvk
            ? <Spinner size="sm" color="primary" aria-label={t("accounts.accountDetailsCards.checkingKvkDetails")} title={t("accounts.accountDetailsCards.checkingKvkDetails")} />
            : kvkError
                ? <i className="mdi mdi-alert-circle-outline text-danger" aria-label={t("accounts.accountDetailsCards.kvkCheckFailed")} title={kvkError} />
                : kvkResult && !kvkResult.isValid
                    ? <i className="mdi mdi-close-circle-outline text-danger" aria-label={t("accounts.accountDetailsCards.kvkNumberIsInvalid")} title={t("accounts.accountDetailsCards.kvkNumberIsInvalid")} />
                    : accountKvkComparison?.nameMatches === true && accountKvkComparison.addressMatches === true
                        ? <i className="mdi mdi-check-circle text-success" aria-label={t("accounts.accountDetailsCards.kvkDetailsMatch")} title={t("accounts.accountDetailsCards.companyNameAndAddressMatchTheKvkRegistration")} />
                        : kvkResult
                            ? <i className="mdi mdi-alert-circle text-warning" aria-label={t("accounts.accountDetailsCards.kvkDetailsDiffer")} title={t("accounts.accountDetailsCards.kvkNumberIsValidButTheCompanyNameOrAddressDiffers")} />
                            : null;
    const kvkCheckedAtDate = kvkResult?.checkedAt ? new Date(kvkResult.checkedAt) : null;
    const kvkCheckedAt = kvkCheckedAtDate && !Number.isNaN(kvkCheckedAtDate.getTime())
        ? new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium", timeStyle: "short" }).format(kvkCheckedAtDate)
        : null;
    const applyVatResult = () => {
        if (!vatComparison) return;
        const addresses = draft.addresses.length ? draft.addresses : [emptyAddress()];
        const primaryIndex = Math.max(0, addresses.findIndex(address => address.primary));
        setDraft(current => Object.assign(new Account(), {
            ...current,
            legal_name: vatComparison.registeredName || current.legal_name,
            addresses: addresses.map((address, index) =>
                index === primaryIndex ? { ...address, ...vatComparison.registeredAddress } : address
            ),
        }));
    };
    const applyKvkResult = () => {
        if (!kvkComparison) return;
        const addresses = draft.addresses.length ? draft.addresses : [emptyAddress()];
        const primaryIndex = Math.max(0, addresses.findIndex(address => address.primary));
        setDraft(current => Object.assign(new Account(), {
            ...current,
            legal_name: kvkComparison.registeredName || current.legal_name,
            chamber_of_commerce_number: kvkResult?.kvkNumber || current.chamber_of_commerce_number,
            addresses: addresses.map((address, index) =>
                index === primaryIndex ? { ...address, ...kvkComparison.registeredAddress } : address
            ),
        }));
    };

    return <>
        <SectionCard
            title="accounts.accountDetailsCards.accountInformation"
            icon="mdi-domain"
            editing={editing === "account"}
            saving={saving}
            onEdit={() => startEditing("account")}
            onCancel={cancel}
            onSave={save}
        >
            {editing === "account" ? <>
                <Field label="accounts.accountFormCard.legalName" value={draft.legal_name} onChange={value => set("legal_name", value)} />
                <div className="mb-3">
                    <label className="form-label" htmlFor="account-detail-tenant">{t("common.tenant")}</label>
                    <Input id="account-detail-tenant" type="select" value={draft.tenant} disabled>
                        {tenants.map(tenant => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                    </Input>
                </div>
                <Field
                    label="accounts.accountFormCard.tradeNames"
                    value={draft.trade_names.join(", ")}
                    onChange={value => set("trade_names", value.split(",").map(name => name.trim()).filter(Boolean))}
                />
                <div className="mb-3">
                    <label className="form-label d-block">{t("common.roles")}</label>
                    {(["customer", "supplier"] as AccountRole[]).map(role =>
                        <div className="form-check form-check-inline" key={role}>
                            <Input
                                className="form-check-input"
                                type="checkbox"
                                id={`account-detail-role-${role}`}
                                checked={draft.roles.includes(role)}
                                onChange={() => toggleRole(role)}
                            />
                            <label className="form-check-label text-capitalize" htmlFor={`account-detail-role-${role}`}>
                                {t(role === "customer" ? "common.roles.customer" : "common.roles.supplier")}
                            </label>
                        </div>
                    )}
                </div>
                <Field label="common.email" type="email" value={draft.email} onChange={value => set("email", value)} />
                <Field label="common.phone" value={draft.phone} onChange={value => set("phone", value)} />
                <div className="mb-3">
                    <label className="form-label" htmlFor="account-detail-vat">{t("common.vatNumber")}</label>
                    <div className="input-group">
                        <Input
                            id="account-detail-vat"
                            value={draft.vat_number}
                            onChange={event => {
                                setVatResult(null);
                                setVatError("");
                                set("vat_number", event.target.value);
                            }}
                            onKeyDown={event => {
                                if (event.key === "Enter" && vatLookup) {
                                    event.preventDefault();
                                    checkVat();
                                }
                            }}
                        />
                        {vatLookup && <Button
                            color="outline-primary"
                            type="button"
                            disabled={checkingVat || !draft.vat_number.trim()}
                            onClick={checkVat}
                        >{t(checkingVat ? "common.actions.checking" : "common.actions.checkVat")}</Button>}
                    </div>
                </div>
                {vatError && <div className="alert alert-danger py-2">{vatError}</div>}
                {vatResult && !vatResult.isValid && <div className="alert alert-danger py-2">{t("common.vatNumberIsNotValid")}</div>}
                {vatComparison && <div className={`alert py-2 ${vatComparison.nameMatches && vatComparison.addressMatches ? "alert-success" : "alert-warning"}`}>
                    <div>
                        <strong>{t("accounts.accountDetailsCards.registeredCompany")}</strong> {vatComparison.registeredName || t("accounts.accountDetailsCards.notReturned")}
                        {vatComparison.nameMatches === true && <span className="text-success ms-1">✓ {t("accounts.accountDetailsCards.matches")}</span>}
                        {vatComparison.nameMatches === false && <span className="text-warning ms-1">— {t("accounts.accountDetailsCards.differs")}</span>}
                    </div>
                    <div>
                        <strong>{t("accounts.accountDetailsCards.registeredAddress")}</strong>{" "}
                        {[
                            vatComparison.registeredAddress.street,
                            vatComparison.registeredAddress.number,
                            vatComparison.registeredAddress.addition,
                            vatComparison.registeredAddress.postal_code,
                            vatComparison.registeredAddress.city,
                        ].filter(Boolean).join(" ") || t("accounts.accountDetailsCards.notReturned")}
                        {vatComparison.addressMatches === true && <span className="text-success ms-1">✓ {t("accounts.accountDetailsCards.matches")}</span>}
                        {vatComparison.addressMatches === false && <span className="text-warning ms-1">— {t("accounts.accountDetailsCards.differs")}</span>}
                    </div>
                    {(vatComparison.nameMatches === false || vatComparison.addressMatches === false) &&
                        <Button color="warning" size="sm" type="button" className="mt-2" onClick={applyVatResult}>
                            {t("accounts.accountDetailsCards.useRegisteredDetails")}
                        </Button>}
                </div>}
                <div className="mb-3">
                    <label className="form-label" htmlFor="account-detail-kvk">{t("accounts.accountFormCard.chamberOfCommerceNumber")}</label>
                    <div className="input-group">
                        <Input
                            id="account-detail-kvk"
                            value={draft.chamber_of_commerce_number}
                            onChange={event => {
                                setKvkResult(null);
                                setKvkError("");
                                set("chamber_of_commerce_number", event.target.value);
                            }}
                            onKeyDown={event => {
                                if (event.key === "Enter" && kvkLookup) {
                                    event.preventDefault();
                                    checkKvk();
                                }
                            }}
                        />
                        {kvkLookup && <Button
                            color="outline-primary"
                            type="button"
                            disabled={checkingKvk || !draft.chamber_of_commerce_number.trim()}
                            onClick={checkKvk}
                        >{t(checkingKvk ? "common.actions.checking" : "common.actions.checkKvk")}</Button>}
                    </div>
                </div>
                {kvkError && <div className="alert alert-danger py-2">{kvkError}</div>}
                {kvkResult && !kvkResult.isValid && <div className="alert alert-danger py-2">{t("accounts.accountFormCard.kvkNumberIsNotValid")}</div>}
                {kvkComparison && <div className={`alert py-2 ${kvkComparison.nameMatches && kvkComparison.addressMatches ? "alert-success" : "alert-warning"}`}>
                    <div>
                        <strong>{t("accounts.accountDetailsCards.registeredCompany")}</strong> {kvkComparison.registeredName || t("accounts.accountDetailsCards.notReturned")}
                        {kvkComparison.nameMatches === true && <span className="text-success ms-1">✓ {t("accounts.accountDetailsCards.matches")}</span>}
                        {kvkComparison.nameMatches === false && <span className="text-warning ms-1">— {t("accounts.accountDetailsCards.differs")}</span>}
                    </div>
                    <div>
                        <strong>{t("accounts.accountDetailsCards.registeredAddress")}</strong>{" "}
                        {[
                            kvkComparison.registeredAddress.street,
                            kvkComparison.registeredAddress.number,
                            kvkComparison.registeredAddress.addition,
                            kvkComparison.registeredAddress.postal_code,
                            kvkComparison.registeredAddress.city,
                        ].filter(Boolean).join(" ") || t("accounts.accountDetailsCards.notReturned")}
                        {kvkComparison.addressMatches === true && <span className="text-success ms-1">✓ {t("accounts.accountDetailsCards.matches")}</span>}
                        {kvkComparison.addressMatches === false && <span className="text-warning ms-1">— {t("accounts.accountDetailsCards.differs")}</span>}
                    </div>
                    {(kvkComparison.nameMatches === false || kvkComparison.addressMatches === false) &&
                        <Button color="warning" size="sm" type="button" className="mt-2" onClick={applyKvkResult}>
                            {t("accounts.accountDetailsCards.useRegisteredDetails")}
                        </Button>}
                </div>}
            </> : <>
                <Detail label="accounts.accountFormCard.legalName" value={account.legal_name} />
                <Detail label="common.tenant" value={tenantName} />
                <Detail label="accounts.accountFormCard.tradeNames" value={(account.trade_names ?? []).join(", ")} />
                <div className="d-flex justify-content-between gap-3 border-bottom py-2">
                    <span className="text-muted">{t("common.roles")}</span>
                    <span className="d-flex flex-wrap justify-content-end gap-1">
                        {(account.roles ?? []).map(role =>
                            <Badge key={role} color={role === "customer" ? "primary" : "warning"} className="text-capitalize">
                                {t(role === "customer" ? "common.roles.customer" : "common.roles.supplier")}
                            </Badge>
                        )}
                        {!account.roles?.length && "—"}
                    </span>
                </div>
                <Detail label="common.email" value={account.email} />
                <Detail label="common.phone" value={account.phone} />
                <Detail label="common.vatNumber" value={<div className="d-flex flex-column align-items-end">
                    <span className="d-inline-flex align-items-center gap-2">
                        {account.vat_number || "—"}
                        {vatStatus}
                    </span>
                    {vatError && <div className="small text-danger mt-1">{vatError}</div>}
                    {vatResult && !vatResult.isValid && <div className="small text-danger mt-1">
                        {t("accounts.accountDetailsCards.vatNumberIsInvalid")}
                    </div>}
                    {vatResult?.isValid && accountVatComparison && <MismatchDetails
                        comparison={accountVatComparison}
                        title="accounts.accountDetailsCards.vatDetailsDiffer"
                    />}
                </div>} />
                <Detail label="accounts.accountDetailsCards.cocNumber" value={<div className="d-flex flex-column align-items-end">
                    <span className="d-inline-flex align-items-center gap-2">
                        {account.chamber_of_commerce_number || "—"}
                        {kvkStatus}
                    </span>
                    {kvkError && <div className="small text-danger mt-1">{kvkError}</div>}
                    {kvkResult && !kvkResult.isValid && <div className="small text-danger mt-1">
                        {t("accounts.accountDetailsCards.kvkNumberIsInvalid")}
                    </div>}
                    {kvkCheckedAt && <div className="account-kvk-checked-at small text-muted mt-1">
                        {t("accounts.accountDetailsCards.kvkCheckedAt", { date: kvkCheckedAt })}
                    </div>}
                    {kvkResult?.isValid && accountKvkComparison && <MismatchDetails
                        comparison={accountKvkComparison}
                        title="accounts.accountDetailsCards.kvkDetailsDiffer"
                    />}
                </div>} />
            </>}
        </SectionCard>

        <SectionCard
            title="accounts.accountFormCard.addresses"
            icon="mdi-map-marker-outline"
            editing={editing === "addresses"}
            saving={saving}
            onEdit={() => {
                startEditing("addresses");
                if (!account.addresses?.length) setDraft(current => Object.assign(new Account(), {
                    ...current,
                    addresses: [emptyAddress()],
                }));
            }}
            onCancel={cancel}
            onSave={save}
        >
            {editing === "addresses" ? <>
                {draft.addresses.map((address, index) =>
                    <div className="border rounded p-3 mb-3" key={address.id ?? index}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{address.label || t("accounts.accountDetailsCards.addressNumber", {number: index + 1})}</strong>
                            <Button
                                color="light"
                                size="sm"
                                className="text-danger"
                                type="button"
                                disabled={draft.addresses.length === 1}
                                onClick={() => set("addresses", draft.addresses.filter((_, current) => current !== index))}
                                aria-label={t("accounts.accountDetailsCards.deleteAddressNumber", {number: index + 1})}
                            >
                                <i className="mdi mdi-delete-outline" />
                            </Button>
                        </div>
                        <Field label="common.label" value={address.label} onChange={value => setAddress(index, "label", value)} />
                        <Field label="common.street" value={address.street} onChange={value => setAddress(index, "street", value)} />
                        <div className="row g-2">
                            <div className="col-7">
                                <Field label="common.number" value={address.number} onChange={value => setAddress(index, "number", value)} />
                            </div>
                            <div className="col-5">
                                <Field label="accounts.accountFormCard.addition" value={address.addition} onChange={value => setAddress(index, "addition", value)} />
                            </div>
                        </div>
                        <Field label="accounts.accountFormCard.postalCode" value={address.postal_code} onChange={value => setAddress(index, "postal_code", value)} />
                        <Field label="common.city" value={address.city} onChange={value => setAddress(index, "city", value)} />
                        <Field label="common.region" value={address.region} onChange={value => setAddress(index, "region", value)} />
                        <Field label="accounts.accountFormCard.country" value={address.country} onChange={value => setAddress(index, "country", value.toUpperCase())} />
                        <div className="form-check">
                            <Input
                                id={`account-address-primary-${index}`}
                                type="checkbox"
                                checked={address.primary}
                                onChange={event => set("addresses", draft.addresses.map((current, currentIndex) => ({
                                    ...current,
                                    primary: currentIndex === index ? event.target.checked : event.target.checked ? false : current.primary,
                                })))}
                            />
                            <label className="form-check-label" htmlFor={`account-address-primary-${index}`}>{t("accounts.accountDetailsCards.primaryAddress")}</label>
                        </div>
                    </div>
                )}
                <Button
                    color="light"
                    size="sm"
                    type="button"
                    onClick={() => set("addresses", [...draft.addresses, {
                        ...emptyAddress(),
                        label: "",
                        primary: false,
                    }])}
                >
                    <i className="mdi mdi-plus me-1" />
                    {t("accounts.accountDetailsCards.addAddress")}
                </Button>
            </> : <>
                {(account.addresses ?? []).map((address, index) =>
                    <div className="border-bottom py-3" key={address.id ?? index}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <strong>{address.label || t("accounts.accountDetailsCards.addressNumber", {number: index + 1})}</strong>
                            {address.primary && <Badge color="light" className="text-muted">{t("accounts.accountDetailsCards.primary")}</Badge>}
                        </div>
                        <div>{[address.street, address.number, address.addition].filter(Boolean).join(" ") || "—"}</div>
                        <div>{[address.postal_code, address.city].filter(Boolean).join(" ")}</div>
                        <div className="text-muted">{[address.region, address.country].filter(Boolean).join(", ")}</div>
                    </div>
                )}
                {!account.addresses?.length && <p className="text-muted mt-3 mb-0">{t("accounts.accountDetailsCards.noAddressSaved")}</p>}
            </>}
        </SectionCard>

        <SectionCard
            title="accounts.accountFormCard.bankAccounts"
            icon="mdi-bank-outline"
            editing={editing === "bank_accounts"}
            saving={saving}
            onEdit={() => {
                startEditing("bank_accounts");
                if (!account.bank_accounts?.length) setDraft(current => Object.assign(new Account(), {
                    ...current,
                    bank_accounts: [emptyBankAccount()],
                }));
            }}
            onCancel={cancel}
            onSave={save}
        >
            {editing === "bank_accounts" ? <>
                {draft.bank_accounts.map((bank, index) =>
                    <div className="border rounded p-3 mb-3" key={bank.id ?? index}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{bank.label || t("accounts.accountDetailsCards.bankAccountNumber", {number: index + 1})}</strong>
                            <Button
                                color="light"
                                size="sm"
                                className="text-danger"
                                type="button"
                                disabled={draft.bank_accounts.length === 1}
                                onClick={() => set("bank_accounts", draft.bank_accounts.filter((_, current) => current !== index))}
                                aria-label={t("accounts.accountDetailsCards.deleteBankAccountNumber", {number: index + 1})}
                            >
                                <i className="mdi mdi-delete-outline" />
                            </Button>
                        </div>
                        <Field label="common.label" value={bank.label} onChange={value => setBank(index, "label", value)} />
                        <Field label="common.iban" value={bank.iban} onChange={value => setBank(index, "iban", value.toUpperCase())} />
                        <Field label="common.bic" value={bank.bic} onChange={value => setBank(index, "bic", value.toUpperCase())} />
                        <Field label="accounts.accountFormCard.accountName" value={bank.account_name} onChange={value => setBank(index, "account_name", value)} />
                        <div className="form-check">
                            <Input
                                id={`account-bank-primary-${index}`}
                                type="checkbox"
                                checked={bank.primary}
                                onChange={event => set("bank_accounts", draft.bank_accounts.map((current, currentIndex) => ({
                                    ...current,
                                    primary: currentIndex === index ? event.target.checked : event.target.checked ? false : current.primary,
                                })))}
                            />
                            <label className="form-check-label" htmlFor={`account-bank-primary-${index}`}>{t("accounts.accountDetailsCards.primaryBankAccount")}</label>
                        </div>
                    </div>
                )}
                <Button
                    color="light"
                    size="sm"
                    type="button"
                    onClick={() => set("bank_accounts", [...draft.bank_accounts, {
                        ...emptyBankAccount(),
                        label: "",
                        primary: false,
                    }])}
                >
                    <i className="mdi mdi-plus me-1" />
                    {t("accounts.accountDetailsCards.addBankAccount")}
                </Button>
            </> : <>
                {(account.bank_accounts ?? []).map((bank, index) =>
                    <div className="border-bottom py-3" key={bank.id ?? index}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <strong>{bank.label || t("accounts.accountDetailsCards.bankAccountNumber", {number: index + 1})}</strong>
                            {bank.primary && <Badge color="light" className="text-muted">{t("accounts.accountDetailsCards.primary")}</Badge>}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="font-monospace text-break">{bank.iban || "—"}</span>
                            {!!normalizeIban(bank.iban) && !isValidIban(bank.iban) &&
                                <i
                                    className="mdi mdi-close-circle-outline text-danger"
                                    aria-label={t("accounts.accountDetailsCards.ibanIsInvalid")}
                                    title={t("accounts.accountDetailsCards.ibanIsInvalid")}
                                />
                            }
                        </div>
                        <div className="text-muted small">{[bank.account_name, bank.bic].filter(Boolean).join(" · ")}</div>
                    </div>
                )}
                {!account.bank_accounts?.length && <p className="text-muted mt-3 mb-0">{t("accounts.accountDetailsCards.noBankAccountSaved")}</p>}
            </>}
        </SectionCard>

        <SectionCard
            title="accounts.accountDetailsCards.billingParameters"
            icon="mdi-file-document-outline"
            editing={editing === "billing"}
            saving={saving}
            onEdit={() => startEditing("billing")}
            onCancel={cancel}
            onSave={save}
        >
            {editing === "billing" ? <>
                <Field label="accounts.accountFormCard.invoiceEmail" type="email" value={draft.billing.invoice_email} onChange={value => setBilling("invoice_email", value)} />
                <label className="form-label d-block mb-3">
                    {t("common.defaultPaymentTerm")}
                    <Input
                        className="mt-2"
                        type="select"
                        value={draft.billing.default_deadline_type}
                        onChange={event => setBilling(
                            "default_deadline_type",
                            event.target.value as AccountBillingSettings["default_deadline_type"]
                        )}
                    >
                        <option value="fixed">{t("common.fixedDeadline")}</option>
                        <option value="pay_when_paid">{t("common.payWhenPaid")}</option>
                    </Input>
                </label>
                {draft.billing.default_deadline_type !== "pay_when_paid" && <Field
                    label="accounts.accountFormCard.paymentDeadlineDays"
                    type="number"
                    value={draft.billing.default_deadline}
                    onChange={value => setBilling("default_deadline", Number(value))}
                />}
                <InvoiceIntervalField
                    value={draft.billing.invoice_interval.cron}
                    timezone={draft.billing.invoice_interval.timezone}
                    onChange={cron => setBilling("invoice_interval", {...draft.billing.invoice_interval, cron})}
                    onTimezoneChange={timezone => setBilling("invoice_interval", {...draft.billing.invoice_interval, timezone})}
                />
                <Field label="accounts.accountFormCard.currency" value={draft.billing.currency} onChange={value => setBilling("currency", value.toUpperCase())} />
                <div className="form-check mb-3">
                    <Input
                        id="account-billing-default-vat-reverse-charged"
                        type="checkbox"
                        checked={draft.billing.default_vat_reverse_charged ?? false}
                        onChange={event => setBilling("default_vat_reverse_charged", event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="account-billing-default-vat-reverse-charged">
                        {t("accounts.accountDetailsCards.reverseChargeVatByDefault")}
                    </label>
                </div>
            </> : <>
                <Detail label="accounts.accountFormCard.invoiceEmail" value={account.billing?.invoice_email} />
                <Detail
                    label="common.defaultPaymentTerm"
                    value={account.billing?.default_deadline_type === "pay_when_paid"
                        ? t("common.payWhenPaid")
                        : t("accounts.accountDetailsCards.countDays", {count: account.billing?.default_deadline ?? 0})}
                />
                <Detail
                    label="accounts.invoiceIntervalField.invoiceInterval"
                    value={invoiceInterval.cron.trim()
                        ? `${describeInvoiceInterval(invoiceInterval.cron, locale)
                            ?? t("accounts.invoiceIntervalField.invalid")} · ${invoiceInterval.timezone}`
                        : t("accounts.invoiceIntervalField.disabledDescription")}
                />
                <Detail label="accounts.accountFormCard.currency" value={account.billing?.currency} />
                <Detail label="accounts.accountDetailsCards.defaultVatTreatment" value={t(account.billing?.default_vat_reverse_charged ? "accounts.accountDetailsCards.reverseCharged" : "accounts.accountDetailsCards.chargeLineVatRates")} />
            </>}
        </SectionCard>

        <SectionCard
            title="accounts.accountFormCard.workerRates"
            icon="mdi-cash-multiple"
            editing={editing === "worker_rates"}
            saving={saving}
            onEdit={() => startEditing("worker_rates")}
            onCancel={cancel}
            onSave={save}
        >
            {editing === "worker_rates" ? <>
                <p className="text-muted">
                    {t("accounts.accountDetailsCards.internalDefaultsUsedForApprovedProjectHoursWorkersCannotViewTheseAmounts")}
                </p>
                <label className="form-label d-block mb-3" htmlFor="worker-hourly-rate">
                    {t("accounts.accountDetailsCards.hourlyRate")}
                    <div className="input-group mt-2">
                        <span className="input-group-text">€</span>
                        <MinorUnitInput
                            id="worker-hourly-rate"
                            min={0}
                            locale={intlLocale}
                            value={draft.worker_rates.hourly_rate}
                            onValueChange={value => setWorkerRates("hourly_rate", value)}
                        />
                        <span className="input-group-text">{t("common.perHour")}</span>
                    </div>
                </label>
                <label className="form-label d-block mb-3" htmlFor="worker-platform-fee-type">
                    {t("common.platformFeeType")}
                    <Input
                        className="mt-2"
                        id="worker-platform-fee-type"
                        type="select"
                        value={draft.worker_rates.platform_fee_type}
                        onChange={event => setWorkerRates(
                            "platform_fee_type",
                            event.target.value as AccountWorkerRates["platform_fee_type"],
                        )}
                    >
                        <option value="percentage">{t("common.percentageOfHourlyRate")}</option>
                        <option value="fixed">{t("common.fixedAmountPerHour")}</option>
                    </Input>
                </label>
                <label className="form-label d-block mb-3" htmlFor="worker-platform-fee-value">
                    {t("common.platformFee")}
                    <div className="input-group mt-2">
                        <span className="input-group-text">
                            {draft.worker_rates.platform_fee_type === "percentage" ? "%" : "€"}
                        </span>
                        <MinorUnitInput
                            id="worker-platform-fee-value"
                            min={0}
                            max={draft.worker_rates.platform_fee_type === "percentage" ? 100 : undefined}
                            locale={intlLocale}
                            value={draft.worker_rates.platform_fee_value}
                            onValueChange={value => setWorkerRates("platform_fee_value", value)}
                        />
                        {draft.worker_rates.platform_fee_type === "fixed" &&
                            <span className="input-group-text">{t("common.perHour")}</span>
                        }
                    </div>
                </label>
            </> : <>
                <Detail
                    label="accounts.accountDetailsCards.hourlyRate"
                    value={new Intl.NumberFormat(intlLocale, {
                        style: "currency",
                        currency: account.billing?.currency || "EUR",
                    }).format((account.worker_rates?.hourly_rate ?? 0) / 100) + ` ${t("common.perHour")}`}
                />
                <Detail
                    label="common.platformFee"
                    value={account.worker_rates?.platform_fee_type === "fixed"
                        ? new Intl.NumberFormat(intlLocale, {
                            style: "currency",
                            currency: account.billing?.currency || "EUR",
                        }).format((account.worker_rates?.platform_fee_value ?? 0) / 100) + ` ${t("common.perHour")}`
                        : `${((account.worker_rates?.platform_fee_value ?? 0) / 100).toFixed(2)}% ${t("accounts.accountDetailsCards.ofHourlyRate")}`}
                />
            </>}
        </SectionCard>
    </>;
};

export default AccountDetailsCards;
